import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { PageHeader } from "~/components/PageHeader";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { PhoneInput } from "~/components/ui/phone-input";
import { getUserBillingProfile } from "~/lib/server-functions/get-user-billing-profile";
import { upsertUserBillingProfile } from "~/lib/server-functions/upsert-user-billing-profile";

const BillingProfileSchema = z
  .object({
    companyName: z.string().max(255).optional(),
    vatId: z.string().max(255).optional(),
    salutation: z.string().max(255).optional(),
    firstName: z.string().max(255).optional(),
    lastName: z.string().max(255).optional(),
    street: z.string().max(255).optional(),
    houseNumber: z.string().max(50).optional(),
    zipCode: z.string().max(20).optional(),
    city: z.string().max(255).optional(),
    country: z.string().max(255).optional(),
    phoneNumber: z.string().max(255).optional(),
    primaryEmail: z
      .string()
      .min(1, "Primäre E-Mail-Adresse ist erforderlich")
      .email("Ungültige E-Mail-Adresse")
      .max(255),
    sendSeparateBillingEmail: z.boolean(),
    billingEmail: z
      .string()
      .max(255)
      .optional()
      .refine(
        (v) => !v || z.string().email().safeParse(v).success,
        "Ungültige E-Mail-Adresse",
      ),
  })
  .superRefine((data, ctx) => {
    if (data.sendSeparateBillingEmail && !data.billingEmail?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["billingEmail"],
        message: "E-Mail Adresse für Rechnungen ist erforderlich",
      });
    }
  });

const nullIfEmpty = (v: string | undefined) => {
  const trimmed = (v ?? "").trim();
  return trimmed.length ? trimmed : null;
};

export const Route = createFileRoute("/advertiser/_main/billing")({
  component: RouteComponent,
  loader: async () => {
    return await getUserBillingProfile();
  },
});

function RouteComponent() {
  const existing = Route.useLoaderData();
  const queryClient = useQueryClient();
  const router = useRouter();

  const form = useForm<z.infer<typeof BillingProfileSchema>>({
    resolver: zodResolver(BillingProfileSchema),
    defaultValues: {
      companyName: (existing as any)?.companyName ?? "",
      vatId: (existing as any)?.vatId ?? "",
      salutation: (existing as any)?.salutation ?? "",
      firstName: (existing as any)?.firstName ?? "",
      lastName: (existing as any)?.lastName ?? "",
      street: (existing as any)?.street ?? "",
      houseNumber: (existing as any)?.houseNumber ?? "",
      zipCode: (existing as any)?.zipCode ?? "",
      city: (existing as any)?.city ?? "",
      country: (existing as any)?.country ?? "",
      phoneNumber: (existing as any)?.phoneNumber ?? "",
      primaryEmail:
        (existing as any)?.primaryEmail ?? (existing as any)?.billingEmail ?? "",
      sendSeparateBillingEmail: Boolean((existing as any)?.billingEmail),
      billingEmail: (existing as any)?.billingEmail ?? "",
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (values: z.infer<typeof BillingProfileSchema>) => {
      return await upsertUserBillingProfile({
        data: {
          companyName: nullIfEmpty(values.companyName),
          vatId: nullIfEmpty(values.vatId),
          salutation: nullIfEmpty(values.salutation),
          firstName: nullIfEmpty(values.firstName),
          lastName: nullIfEmpty(values.lastName),
          street: nullIfEmpty(values.street),
          houseNumber: nullIfEmpty(values.houseNumber),
          zipCode: nullIfEmpty(values.zipCode),
          city: nullIfEmpty(values.city),
          country: nullIfEmpty(values.country),
          phoneNumber: nullIfEmpty(values.phoneNumber),
          primaryEmail: values.primaryEmail.trim(),
          sendSeparateBillingEmail: values.sendSeparateBillingEmail,
          billingEmail: values.sendSeparateBillingEmail
            ? nullIfEmpty(values.billingEmail)
            : null,
        },
      });
    },
    onSuccess: async () => {
      queryClient.resetQueries();
      await router.invalidate();
      toast.success("Rechnungsdaten gespeichert");
    },
    onError: (err: Error) => {
      toast.error("Fehler", { description: err.message });
    },
  });

  return (
    <div className="w-full mb-6">
      <PageHeader title="Rechnungsdaten" />
      <Form {...form}>
        <form
          className="flex flex-col items-start gap-6 w-full"
          onSubmit={form.handleSubmit(async (values) => {
            await saveMutation.mutateAsync(values);
          })}
          noValidate
        >
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Firmenname</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vatId"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Umsatzsteuer-ID</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="salutation"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Anrede</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Vorname</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Nachname</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="street"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Anschrift</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="houseNumber"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Hausnummer</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            <FormField
              control={form.control}
              name="zipCode"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>PLZ</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Ort</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Land</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Telefonnummer</FormLabel>
                <FormControl>
                  <PhoneInput
                    value={field.value ?? ""}
                    onChange={(val) => field.onChange(val)}
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="primaryEmail"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Primäre E-Mail Adresse</FormLabel>
                <FormControl>
                  <Input {...field} type="email" />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sendSeparateBillingEmail"
            render={({ field }) => (
              <FormItem className="w-full">
                <div className="flex items-start gap-3">
                  <FormControl>
                    <Checkbox
                      checked={!!field.value}
                      onCheckedChange={(checked) => field.onChange(!!checked)}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-normal">
                    Rechnungen an separate E-Mail Adresse senden
                  </FormLabel>
                </div>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          {form.watch("sendSeparateBillingEmail") && (
            <FormField
              control={form.control}
              name="billingEmail"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>E-Mail Adresse für Rechnungen</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />
          )}

          <Button type="submit" loading={saveMutation.isPending}>
            Speichern
          </Button>
        </form>
      </Form>
    </div>
  );
}

